import { Link } from "react-router-dom";
import { ShieldCheck, FileText, Bot, Download, TrendingUp, Building2, Banknote, ArrowRight, CircleDollarSign, Sparkles, Check } from "lucide-react";

const STEPS = [
  {
    n: 1,
    icon: FileText,
    title: "Fill Out Form",
    body: "Submit your business profile through our secure online form — entity, NAICS, revenue, FICO band, target funding.",
  },
  {
    n: 2,
    icon: Bot,
    title: "Run WealthFirst FundingBot Agent",
    body: "Our Claude-Haiku underwriting agent scores your profile against banks, family offices, CDFIs, SBA, VCs, and private credit.",
  },
  {
    n: 3,
    icon: Download,
    title: "Download WealthFirst AI Report",
    body: "Receive your personalized business funding plan: approval score, matched lenders, fixable gaps, and a 90-day roadmap.",
  },
];

const VALUE_PROPS = [
  { icon: ShieldCheck, title: "See what banks see", body: "Before partners ever apply — review your underwriter-facing profile and fix what holds you back." },
  { icon: TrendingUp, title: "Funding odds, not guesses", body: "An approval-score 0-100 so you know which channels are realistic — and which to skip." },
  { icon: Building2, title: "WealthFirst Lenders List", body: "Curated matches from UHNW family offices, CDFIs, SBA programs, commercial banks, and Net-30 tradelines." },
  { icon: Banknote, title: "Position for $80K – $350K", body: "Build credit depth with 4–5 reporting tradelines and the right commercial banking relationships." },
];

const GOALS_90_DAYS = [
  "Establish 4–5 Net-30 reporting tradelines (Uline · Quill · Grainger · Crown Office · Wise Business Plans)",
  "Strengthen primary commercial banking relationship — minimum $10K average daily balance, 90-day seasoning",
  "Apply for first business credit tier — Bank of America Business Advantage Card recommended for FICO ≥ 670",
  "Build D&B Paydex score to ≥ 80 and ensure SBFE reporting is active",
  "Compliance setup — commercial address, dedicated 800/toll-free phone, NAICS code optimization, DUNS registration",
];

const COMPLIANCE_ITEMS = [
  { item: "Commercial Address", required: "Required" },
  { item: "Dedicated 800 / toll-free phone #", required: "Required" },
  { item: "NAICS Code Optimization", required: "Required" },
  { item: "EIN + Sec-of-State Good Standing", required: "Required" },
  { item: "DUNS Number", required: "Required" },
  { item: "Business Bank Account + 90-day seasoning", required: "Required" },
  { item: "4–5 Net-C reporting tradelines", required: "Required" },
  { item: "D&B Paydex ≥ 80", required: "Recommended" },
  { item: "Operating Agreement / Bylaws on file", required: "Recommended" },
  { item: "Business website with SSL + branded domain email", required: "Recommended" },
];

export default function CreditReadiness() {
  return (
    <div data-testid="credit-readiness-page" className="pt-24">
      {/* HERO */}
      <section className="px-6 pt-12 pb-20" style={{ background: "var(--bg)" }}>
        <div className="max-w-5xl mx-auto">
          <p className="eyebrow mb-5" style={{ color: "var(--brass)" }}>
            WealthFirst AI Diagnostic · Not a Funding Guarantee
          </p>
          <h1 className="font-display leading-[0.95] text-5xl md:text-7xl mb-7">
            Build your business credit the way a
            <em style={{ color: "var(--brass)" }}> bank underwriter</em> wants it done.
          </h1>
          <p className="max-w-3xl text-base md:text-lg mb-10" style={{ color: "var(--ink-muted)", lineHeight: 1.65 }}>
            See exactly what banks, family offices, CDFIs, SBA lenders, and private-equity desks see — <em>before</em> you
            apply for funding. Our WealthFirst AI Diagnostic identifies fixable regulatory & corporate-structure gaps,
            scores your funding odds, and hands you a curated WealthFirst Lenders List with a 90-day action roadmap.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/credit-readiness/apply" className="btn-brass" data-testid="cr-cta-start">
              Start My Diagnostic <ArrowRight size={14} />
            </Link>
            <Link
              to="/funding/compare"
              className="btn-brass"
              style={{ background: "transparent", color: "var(--brass)" }}
            >
              Compare Funding Plans
            </Link>
          </div>
          <p className="mt-8 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--brass)" }}>
            <Sparkles size={12} style={{ display: "inline", marginRight: 8 }} />
            Powered by Claude Haiku 4.5 underwriting agent · Personalized in minutes
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow mb-4">How It Works</p>
          <h2 className="font-display text-4xl md:text-5xl mb-14 max-w-3xl">
            Three steps. No guesswork. <em style={{ color: "var(--brass)" }}>Approval-ready.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="p-8 relative"
                style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}
                data-testid={`cr-step-${s.n}`}
              >
                <span
                  className="font-display absolute"
                  style={{ top: -22, left: 18, fontSize: 64, color: "var(--brass)", opacity: 0.4, lineHeight: 1 }}
                >
                  {s.n}
                </span>
                <s.icon size={24} style={{ color: "var(--brass)", marginBottom: 16 }} />
                <h3 className="font-display text-2xl mb-3">{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="px-6 py-24" style={{ background: "var(--surface)" }}>
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow mb-4">Why Members Use This</p>
          <h2 className="font-display text-4xl md:text-5xl mb-14 max-w-3xl">
            Stop applying blind. <em style={{ color: "var(--brass)" }}>Apply with the deck stacked in your favor.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="p-6" style={{ border: "1px solid var(--hairline)" }}>
                <v.icon size={22} style={{ color: "var(--brass)", marginBottom: 14 }} />
                <h3 className="font-display text-xl mb-2 leading-tight">{v.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE TABLE */}
      <section className="px-6 py-24">
        <div className="max-w-5xl mx-auto">
          <p className="eyebrow mb-4">Compliance Pre-Check</p>
          <h2 className="font-display text-4xl md:text-5xl mb-10 max-w-3xl">
            Fix what underwriters flag — <em style={{ color: "var(--brass)" }}>before</em> they see your application.
          </h2>
          <div style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}>
            {COMPLIANCE_ITEMS.map((c, idx) => (
              <div
                key={c.item}
                className="flex items-center justify-between p-5"
                style={{ borderBottom: idx < COMPLIANCE_ITEMS.length - 1 ? "1px solid var(--hairline)" : "none" }}
              >
                <div className="flex items-center gap-4">
                  <Check size={16} style={{ color: "var(--brass)", flexShrink: 0 }} />
                  <span style={{ color: "var(--ink)" }}>{c.item}</span>
                </div>
                <span
                  className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                  style={{
                    border: "1px solid var(--hairline-strong)",
                    color: c.required === "Required" ? "var(--brass)" : "var(--ink-muted)",
                  }}
                >
                  {c.required}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 90 DAY GOALS */}
      <section className="px-6 py-24" style={{ background: "var(--bg)" }}>
        <div className="max-w-5xl mx-auto">
          <p className="eyebrow mb-4" style={{ color: "var(--brass)" }}>Business Credit Goals — Next 90 Days</p>
          <h2 className="font-display text-4xl md:text-5xl mb-10 leading-tight">
            Position for <em style={{ color: "var(--brass)" }}>$80,000 – $350,000</em> in credit lines.
          </h2>
          <ol className="space-y-5 mb-12 max-w-3xl">
            {GOALS_90_DAYS.map((g, i) => (
              <li key={i} className="flex gap-5">
                <span
                  className="font-display text-3xl shrink-0"
                  style={{ color: "var(--brass)", minWidth: 40 }}
                >
                  0{i + 1}
                </span>
                <p className="text-base leading-relaxed pt-1" style={{ color: "var(--ink-muted)" }}>
                  {g}
                </p>
              </li>
            ))}
          </ol>
          <Link to="/credit-readiness/apply" className="btn-brass" data-testid="cr-cta-bottom">
            Run My WealthFirst AI Diagnostic <ArrowRight size={14} />
          </Link>
          <p className="text-xs mt-6" style={{ color: "var(--ink-faint)" }}>
            <CircleDollarSign size={11} style={{ display: "inline", marginRight: 6 }} />
            This is an AI diagnostic and prevention tool — not a guarantee of funding approval. All underwriting decisions
            remain with individual lenders at their sole discretion.
          </p>
        </div>
      </section>
    </div>
  );
}
